import { useCallback, useEffect, useReducer, useRef } from 'react'
import type {
  CommentHubAPI,
  CommentHubMyComment,
  CommentHubMyCommentsPage,
  CommentHubMyCommentsSort,
} from 'shared-code/commenthub-api'
import { AuthContext } from '../contexts/AuthContext'
import { CommentHubAPIContext } from '../contexts/CommentHubAPIContext'

/**
 * "Your comments" on the account Profile tab (plan §7): the reader's own comments from commentHub
 * `GET /users/me/comments`. Three parts so each is testable without a DOM — the request
 * (`loadYourCommentsPage`), the state (`yourCommentsReducer`), and the hook gluing them. Own
 * comments are private: nothing here runs on the server or without a session token.
 */

export const YOUR_COMMENTS_PAGE_SIZE = 20

export interface YourCommentsState {
  /** idle = no session / nothing asked yet; absent = a commentHub older than the route (404). */
  phase: 'idle' | 'loading' | 'ready' | 'failed' | 'absent'
  sort: CommentHubMyCommentsSort
  items: readonly CommentHubMyComment[]
  /** Null until the first page answers — the section draws nothing before it knows the route
   *  exists, so an older commentHub never flashes an empty card. */
  total: number | null
  postCount: number
  nextCursor: string | null
  /** "Show 20 more": its own state, so a failed next page keeps the rows on screen. */
  more: 'idle' | 'loading' | 'failed'
}

export type YourCommentsAction =
  | { type: 'reset' }
  | { type: 'start'; sort: CommentHubMyCommentsSort }
  | { type: 'more' }
  | { type: 'page'; page: CommentHubMyCommentsPage; append: boolean }
  | { type: 'failed'; append: boolean }
  | { type: 'absent' }

export function initialYourComments(): YourCommentsState {
  return {
    phase: 'idle',
    sort: 'newest',
    items: [],
    total: null,
    postCount: 0,
    nextCursor: null,
    more: 'idle',
  }
}

export function yourCommentsReducer(
  state: YourCommentsState,
  action: YourCommentsAction
): YourCommentsState {
  switch (action.type) {
    case 'reset':
      return initialYourComments()
    case 'start':
      // A new sort is a new list: its cursor means nothing under another order.
      return {
        ...state,
        phase: 'loading',
        sort: action.sort,
        items: [],
        nextCursor: null,
        more: 'idle',
      }
    case 'more':
      return { ...state, more: 'loading' }
    case 'page': {
      const { page } = action
      let items: readonly CommentHubMyComment[] = page.items
      if (action.append) {
        const seen = new Set(state.items.map(c => c.id))
        items = [...state.items, ...page.items.filter(c => !seen.has(c.id))]
      }
      return {
        ...state,
        phase: 'ready',
        items,
        total: page.total,
        postCount: page.postCount,
        nextCursor: page.nextCursor,
        more: 'idle',
      }
    }
    case 'failed':
      return action.append ? { ...state, more: 'failed' } : { ...state, phase: 'failed', items: [] }
    case 'absent':
      return { ...initialYourComments(), phase: 'absent' }
    default:
      return state
  }
}

export type YourCommentsResult =
  | { kind: 'page'; page: CommentHubMyCommentsPage }
  | { kind: 'absent' }
  | { kind: 'failed' }
  | { kind: 'aborted' }

/**
 * One page of the reader's comments, or null WITHOUT a request when there is no api or no session
 * token — a signed-out reader costs commentHub nothing. `site` is sent only when set (an empty
 * one is a 400). Not routed through `withAuth`: a read on page open must not pop the sign-in
 * dialog on a 401 — it fails quietly to "Try again", like the bell's badge poll.
 */
export async function loadYourCommentsPage(
  api: CommentHubAPI | null,
  token: string | null | undefined,
  query: { site?: string | null; sort: CommentHubMyCommentsSort; cursor?: string | null },
  signal?: AbortSignal
): Promise<YourCommentsResult | null> {
  if (!api || !token) {
    return null
  }
  try {
    const page = await api.getMyComments(
      token,
      {
        site: query.site || undefined,
        sort: query.sort,
        cursor: query.cursor || undefined,
        limit: YOUR_COMMENTS_PAGE_SIZE,
      },
      signal
    )
    return page ? { kind: 'page', page } : { kind: 'absent' }
  } catch (e) {
    return signal?.aborted ? { kind: 'aborted' } : { kind: 'failed' }
  }
}

export interface UseYourCommentsResult {
  state: YourCommentsState
  /** The app's origin (`CommentHubAPIContext.appUrl`): the only absolute host a row links to. */
  appUrl: string | null
  setSort: (sort: CommentHubMyCommentsSort) => void
  showMore: () => void
  retry: () => void
}

export function useYourComments(): UseYourCommentsResult {
  // Optional containers: without a provider (a spec, a layout that forgot it) the section is
  // inert — no token, no request — rather than a throw.
  const commentHub = CommentHubAPIContext.useOptionalContainer()
  const api = commentHub?.api ?? null
  const site = commentHub?.site ?? null
  const appUrl = commentHub?.appUrl ?? null
  // Keyed on the token string, never the authState object (a new identity on every
  // applySessionUser — use-commenthub-profile's refetch loop).
  const token = AuthContext.useOptionalContainer()?.authState?.token ?? null

  const [state, dispatch] = useReducer(yourCommentsReducer, undefined, initialYourComments)
  // The newest request wins: a slow answer for an older sort or session must not land.
  const genRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)

  const fetchPage = useCallback(
    (sort: CommentHubMyCommentsSort, cursor: string | null) => {
      if (!api || !token) {
        return
      }
      const gen = ++genRef.current
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const append = cursor !== null
      dispatch(append ? { type: 'more' } : { type: 'start', sort })
      void loadYourCommentsPage(api, token, { site, sort, cursor }, controller.signal).then(
        result => {
          if (gen !== genRef.current || !result || result.kind === 'aborted') {
            return
          }
          if (result.kind === 'page') {
            dispatch({ type: 'page', page: result.page, append })
          } else if (result.kind === 'absent') {
            dispatch({ type: 'absent' })
          } else {
            dispatch({ type: 'failed', append })
          }
        }
      )
    },
    [api, token, site]
  )

  useEffect(() => {
    // A new session (or none) is a new list, newest first.
    dispatch({ type: 'reset' })
    fetchPage('newest', null)
    return () => {
      genRef.current += 1
      abortRef.current?.abort()
    }
  }, [fetchPage])

  return {
    state,
    appUrl,
    setSort: sort => {
      if (sort !== state.sort || state.phase === 'failed') {
        fetchPage(sort, null)
      }
    },
    showMore: () => {
      if (state.nextCursor && state.more !== 'loading') {
        fetchPage(state.sort, state.nextCursor)
      }
    },
    retry: () => fetchPage(state.sort, null),
  }
}
